import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Checkbox from '../../../components/ui/Checkbox';
import tradingApi from '../../../services/tradingApi';
import useLanguage from '../../../hooks/useLanguage';
import { useToast } from '../../../components/ui/Toast';
import EditPositionModal from '../../../components/EditPositionModal';
import PartialCloseModal from '../../../components/PartialCloseModal';

const PositionsPanel = () => {
  const [activeTab, setActiveTab] = useState('positions');
  const [positions, setPositions] = useState([]);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [tradeHistory, setTradeHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [selectedPositions, setSelectedPositions] = useState([]);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [partialCloseModalOpen, setPartialCloseModalOpen] = useState(false);
  const [editingPosition, setEditingPosition] = useState(null);
  const [closingPosition, setClosingPosition] = useState(null);
  const { t, isRTL } = useLanguage();
  const toast = useToast();

  // Handle new positions from TradingModal
  useEffect(() => {
    const handleNewPosition = (event) => {
      const newPosition = event.detail;
      console.log('🔥 NEW POSITION EVENT:', newPosition);
      
      if (newPosition.status === 'open') {
        // Add to positions
        setPositions(prev => {
          const updated = [newPosition, ...prev];
          console.log('✅ POSITIONS UPDATED:', updated);
          return updated;
        });
      } else {
        // Add to pending orders
        setPendingOrders(prev => {
          const updated = [newPosition, ...prev];
          console.log('✅ PENDING ORDERS UPDATED:', updated);
          return updated;
        });
      }
    };

    console.log('🎯 Event listener registered');
    window.addEventListener('newPosition', handleNewPosition);

    return () => {
      console.log('🔴 Event listener removed');
      window.removeEventListener('newPosition', handleNewPosition);
    };
  }, []);

  const handlePositionUpdate = useCallback((data) => {
    setPositions(prev => {
      const index = prev?.findIndex(p => p?.id === data?.id);
      if (index >= 0) {
        // Update existing position
        const updated = [...prev];
        updated[index] = { ...updated?.[index], ...data };
        return updated;
      } else if (data?.status === 'open') {
        // Add new position
        return [...prev, data];
      }
      return prev;
    });
  }, []);

  const handleOrderUpdate = useCallback((data) => {
    setPendingOrders(prev => {
      const index = prev?.findIndex(o => o?.id === data?.id);
      if (index >= 0) {
        if (data?.status === 'cancelled' || data?.status === 'filled') {
          // Remove cancelled or filled orders
          return prev?.filter(o => o?.id !== data?.id);
        } else {
          // Update existing order
          const updated = [...prev];
          updated[index] = { ...updated?.[index], ...data };
          return updated;
        }
      } else if (data?.status === 'pending') {
        // Add new pending order
        return [...prev, data];
      }
      return prev;
    });
  }, []);

  const loadTradingData = async () => {
    try {
      setLoading(true);
      
      // Load positions, orders, and history from Zonex API
      const [positionsResponse, ordersResponse, historyResponse] = await Promise.allSettled([
        tradingApi?.getPositions(),
        tradingApi?.getPendingOrders(),
        tradingApi?.getTradeHistory(50)
      ]);

      // Process positions with additional mock data for better table display
      if (positionsResponse?.status === 'fulfilled' && positionsResponse?.value) {
        setPositions(positionsResponse?.value);
      } else {
        // Enhanced mock positions with all required fields
        setPositions([
          {
            id: 1,
            symbol: 'EURUSD',
            type: 'buy',
            amount: 0.1,
            openPrice: 1.1675,
            currentPrice: 1.1680,
            closePrice: null,
            profit: 5.00,
            swap: -0.25,
            commission: 0,
            stopLoss: null,
            takeProfit: 1.1700,
            openTime: new Date(Date.now() - 2 * 60 * 60 * 1000)
          },
          {
            id: 2,
            symbol: 'GBPUSD',
            type: 'sell',
            amount: 0.2,
            openPrice: 1.3430,
            currentPrice: 1.3425,
            closePrice: null,
            profit: 10.00,
            swap: 0.15,
            commission: 0,
            stopLoss: 1.3450,
            takeProfit: 1.3400,
            openTime: new Date(Date.now() - 4 * 60 * 60 * 1000)
          },
          {
            id: 3,
            symbol: 'USDJPY',
            type: 'buy',
            amount: 0.15,
            openPrice: 149.25,
            currentPrice: 149.45,
            closePrice: null,
            profit: 15.75,
            swap: -0.45,
            commission: 0,
            stopLoss: 148.50,
            takeProfit: 150.00,
            openTime: new Date(Date.now() - 6 * 60 * 60 * 1000)
          },
          {
            id: 4,
            symbol: 'XAUUSD',
            type: 'sell',
            amount: 0.05,
            openPrice: 1935.50,
            currentPrice: 1930.25,
            closePrice: null,
            profit: 26.25,
            swap: 0,
            commission: 0,
            stopLoss: 1945.00,
            takeProfit: 1920.00,
            openTime: new Date(Date.now() - 8 * 60 * 60 * 1000)
          }
        ]);
      }

      // Process pending orders with enhanced data
      if (ordersResponse?.status === 'fulfilled' && ordersResponse?.value) {
        setPendingOrders(ordersResponse?.value);
      } else {
        // Enhanced mock orders with diverse trading pairs
        setPendingOrders([
          {
            id: 5,
            symbol: 'AUDUSD',
            type: 'buy_limit',
            amount: 0.25,
            openPrice: 0.6520,
            currentPrice: 0.6545,
            closePrice: null,
            profit: 0,
            swap: 0,
            commission: 0,
            stopLoss: 0.6500,
            takeProfit: 0.6580,
            openTime: new Date(Date.now() - 30 * 60 * 1000)
          },
          {
            id: 6,
            symbol: 'NZDUSD',
            type: 'sell_stop',
            amount: 0.1,
            openPrice: 0.5850,
            currentPrice: 0.5875,
            closePrice: null,
            profit: 0,
            swap: 0,
            commission: 0,
            stopLoss: 0.5900,
            takeProfit: 0.5800,
            openTime: new Date(Date.now() - 45 * 60 * 1000)
          },
          {
            id: 7,
            symbol: 'USDCAD',
            type: 'buy_stop',
            amount: 0.3,
            openPrice: 1.3620,
            currentPrice: 1.3595,
            closePrice: null,
            profit: 0,
            swap: 0,
            commission: 0,
            stopLoss: 1.3570,
            takeProfit: 1.3680,
            openTime: new Date(Date.now() - 60 * 60 * 1000)
          },
          {
            id: 8,
            symbol: 'EURGBP',
            type: 'sell_limit',
            amount: 0.15,
            openPrice: 0.8735,
            currentPrice: 0.8742,
            closePrice: null,
            profit: 0,
            swap: 0,
            commission: 0,
            stopLoss: 0.8765,
            takeProfit: 0.8700,
            openTime: new Date(Date.now() - 90 * 60 * 1000)
          },
          {
            id: 9,
            symbol: 'CHFJPY',
            type: 'buy_limit',
            amount: 0.08,
            openPrice: 165.80,
            currentPrice: 165.95,
            closePrice: null,
            profit: 0,
            swap: 0,
            commission: 0,
            stopLoss: 165.30,
            takeProfit: 166.50,
            openTime: new Date(Date.now() - 2 * 60 * 60 * 1000)
          }
        ]);
      }

      // Process trade history
      if (historyResponse?.status === 'fulfilled' && historyResponse?.value) {
        setTradeHistory(historyResponse?.value);
      } else {
        // Fallback mock history
        setTradeHistory([
          {
            id: 100,
            symbol: 'USDJPY',
            type: 'buy',
            volume: 0.15,
            openPrice: 149.25,
            closePrice: 149.45,
            profit: 15.75,
            swap: -0.45,
            commission: 0,
            openTime: new Date(Date.now() - 24 * 60 * 60 * 1000),
            closeTime: new Date(Date.now() - 22 * 60 * 60 * 1000)
          },
          {
            id: 101,
            symbol: 'AUDUSD',
            type: 'sell',
            volume: 0.25,
            openPrice: 0.6545,
            closePrice: 0.6530,
            profit: 37.50,
            swap: 0.25,
            commission: 0,
            openTime: new Date(Date.now() - 48 * 60 * 60 * 1000),
            closeTime: new Date(Date.now() - 45 * 60 * 60 * 1000)
          },
          {
            id: 102,
            symbol: 'EURUSD',
            type: 'buy',
            volume: 0.1,
            openPrice: 1.1650,
            closePrice: 1.1630,
            profit: -20.00,
            swap: 0,
            commission: 0,
            openTime: new Date(Date.now() - 72 * 60 * 60 * 1000),
            closeTime: new Date(Date.now() - 70 * 60 * 60 * 1000)
          },
          {
            id: 103,
            symbol: 'GBPUSD',
            type: 'sell',
            volume: 0.2,
            openPrice: 1.3450,
            closePrice: 1.3420,
            profit: 60.00,
            swap: -0.15,
            commission: 0,
            openTime: new Date(Date.now() - 96 * 60 * 60 * 1000),
            closeTime: new Date(Date.now() - 94 * 60 * 60 * 1000)
          },
          {
            id: 104,
            symbol: 'XAUUSD',
            type: 'buy',
            volume: 0.05,
            openPrice: 1925.50,
            closePrice: 1920.00,
            profit: -27.50,
            swap: 0,
            commission: 0,
            openTime: new Date(Date.now() - 120 * 60 * 60 * 1000),
            closeTime: new Date(Date.now() - 118 * 60 * 60 * 1000)
          }
        ]);
      }

    } catch (error) {
      console.error('Failed to load trading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const connectToWebSocket = () => {
    try {
      setConnectionStatus('connecting');
      
      // Connect to WebSocket for real-time position updates
      tradingApi?.connectWebSocket(
        (data) => handleWebSocketMessage(data),
        (error) => {
          console.error('WebSocket error:', error);
          setConnectionStatus('error');
        },
        (event) => {
          setConnectionStatus('disconnected');
        }
      );

      // Subscribe to position and order updates
      setTimeout(() => {
        if (tradingApi?.getConnectionStatus() === 'connected') {
          tradingApi?.subscribe('positions', null, handlePositionUpdate);
          tradingApi?.subscribe('orders', null, handleOrderUpdate);
          setConnectionStatus('connected');
        }
      }, 1000);

    } catch (error) {
      console.error('WebSocket connection failed:', error);
      setConnectionStatus('error');
    }
  };

  // Initialize data and websocket
  useEffect(() => {
    loadTradingData();
    connectToWebSocket();

    return () => {
      tradingApi?.disconnectWebSocket();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update positions with real-time price data
  const updatePositionsWithPrice = useCallback((priceData) => {
    setPositions(prev => prev.map(position => {
      if (position.symbol === priceData.symbol) {
        const currentPrice = position.type === 'buy' ? priceData.bid : priceData.ask;
        const priceDiff = position.type === 'buy' 
          ? (currentPrice - position.openPrice)
          : (position.openPrice - currentPrice);
        
        // Calculate profit (simplified calculation)
        const pipValue = position.symbol.includes('JPY') ? 0.01 : 0.0001;
        const pips = priceDiff / pipValue;
        const profit = pips * position.volume * 10; // Simplified P&L calculation
        
        return {
          ...position,
          currentPrice,
          profit: parseFloat(profit.toFixed(2))
        };
      }
      return position;
    }));
  }, []);

  const handleWebSocketMessage = useCallback((data) => {
    switch (data?.type) {
      case 'position_update':
        handlePositionUpdate(data);
        break;
      case 'order_update':
        handleOrderUpdate(data);
        break;
      case 'price_update':
        // Update positions with new prices
        updatePositionsWithPrice(data);
        break;
      default:
        break;
    }
  }, [handlePositionUpdate, handleOrderUpdate, updatePositionsWithPrice]);

  // Utility functions - must be defined before other callbacks that use them
  const formatPrice = useCallback((price) => {
    if (!price) return '--';
    if (price > 1000) return price?.toFixed(2);
    if (price > 10) return price?.toFixed(4);
    return price?.toFixed(5);
  }, []);

  const formatTime = useCallback((date) => {
    if (!date) return '--';
    return date?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }, []);

  const formatDate = useCallback((date) => {
    if (!date) return '--';
    return date?.toLocaleDateString();
  }, []);

  const getProfitColor = useCallback((profit) => {
    return profit >= 0 ? 'text-green-600' : 'text-red-600';
  }, []);

  const getTypeColor = useCallback((type) => {
    return type?.includes('buy') ? 'text-green-600' : 'text-red-600';
  }, []);

  // Bulk operations
  const toggleSelectPosition = useCallback((positionId) => {
    setSelectedPositions(prev => {
      if (prev.includes(positionId)) {
        return prev.filter(id => id !== positionId);
      } else {
        return [...prev, positionId];
      }
    });
  }, []);

  // Combined data for unified table - must be defined before toggleSelectAll
  // Separate data for each tab
  const filteredPositions = useMemo(() => {
    const filtered = searchTerm
      ? positions?.filter(item => 
          item?.symbol?.toLowerCase()?.includes(searchTerm?.toLowerCase())
        )
      : positions;
    console.log('🔍 FILTERED POSITIONS:', filtered?.length, 'from', positions?.length);
    return filtered;
  }, [positions, searchTerm]);

  const filteredPendingOrders = useMemo(() => {
    const filtered = searchTerm
      ? pendingOrders?.filter(item => 
          item?.symbol?.toLowerCase()?.includes(searchTerm?.toLowerCase())
        )
      : pendingOrders;
    return filtered;
  }, [pendingOrders, searchTerm]);

  const filteredHistory = useMemo(() => {
    const filtered = searchTerm
      ? tradeHistory?.filter(item => 
          item?.symbol?.toLowerCase()?.includes(searchTerm?.toLowerCase())
        )
      : tradeHistory;
    return filtered;
  }, [tradeHistory, searchTerm]);

  // Combined data for positions tab only
  const combinedData = useMemo(() => {
    console.log('📊 COMBINED DATA:', filteredPositions?.length);
    return filteredPositions;
  }, [filteredPositions]);

  const toggleSelectAll = useCallback(() => {
    if (selectedPositions.length === combinedData.length && combinedData.length > 0) {
      setSelectedPositions([]);
    } else {
      setSelectedPositions(combinedData.map(item => item.id));
    }
  }, [selectedPositions.length, combinedData]);

  const closeSelectedPositions = useCallback(async () => {
    const positionsToClose = positions.filter(p => selectedPositions.includes(p.id));
    
    if (positionsToClose.length === 0) {
      toast.warning(t('noPositionsSelected'), 3000);
      return;
    }

    try {
      // Close all selected positions
      for (const position of positionsToClose) {
        await tradingApi?.closePosition(position.id);
        
        // Add to history
        const closedPosition = {
          ...position,
          closeTime: new Date(),
          closePrice: position.currentPrice,
          status: 'closed'
        };
        setTradeHistory(prev => [closedPosition, ...prev]);
      }

      // Remove from positions
      setPositions(prev => prev.filter(p => !selectedPositions.includes(p.id)));
      
      // Clear selection
      setSelectedPositions([]);

      toast.success(
        `${positionsToClose.length} ${t('positionsClosed')}`,
        4000
      );
    } catch (error) {
      console.error('Failed to close positions:', error);
      toast.error(t('bulkCloseFailed'), 4000);
    }
  }, [positions, selectedPositions, toast, t]);

  const cancelSelectedOrders = useCallback(async () => {
    const ordersToCancel = pendingOrders.filter(o => selectedPositions.includes(o.id));
    
    if (ordersToCancel.length === 0) {
      toast.warning(t('noOrdersSelected'), 3000);
      return;
    }

    try {
      // Cancel all selected orders
      for (const order of ordersToCancel) {
        await tradingApi?.cancelOrder(order.id);
      }

      // Remove from pending orders
      setPendingOrders(prev => prev.filter(o => !selectedPositions.includes(o.id)));
      
      // Clear selection
      setSelectedPositions([]);

      toast.success(
        `${ordersToCancel.length} ${t('ordersCancelled')}`,
        4000
      );
    } catch (error) {
      console.error('Failed to cancel orders:', error);
      toast.error(t('bulkCancelFailed'), 4000);
    }
  }, [pendingOrders, selectedPositions, toast, t]);

  const closePosition = useCallback(async (positionId) => {
    try {
      // Find the position details before removing
      const position = positions.find(p => p?.id === positionId);
      
      await tradingApi?.closePosition(positionId);
      
      // Remove from positions list
      setPositions(prev => prev?.filter(p => p?.id !== positionId));
      
      // Add to trade history with close time
      if (position) {
        const closedPosition = {
          ...position,
          closeTime: new Date(),
          closePrice: position.currentPrice,
          status: 'closed'
        };
        setTradeHistory(prev => [closedPosition, ...prev]);

        // Show success toast with position details
        const profitText = position.profit >= 0 ? `+$${position.profit.toFixed(2)}` : `-$${Math.abs(position.profit).toFixed(2)}`;
        toast.success(
          `${position.symbol} ${t('positionClosed')}\n${position.type.toUpperCase()} ${position.amount} ${t('lot')} - ${t('profit')}: ${profitText}`,
          5000
        );
      } else {
        toast.success(t('positionClosed'), 3000);
      }
    } catch (error) {
      console.error('Failed to close position:', error);
      toast.error(t('positionCloseFailed'), 4000);
    }
  }, [toast, t, positions]);

  const editPosition = useCallback((position) => {
    setEditingPosition(position);
    setEditModalOpen(true);
  }, []);

  const handleSavePosition = useCallback((updatedPosition) => {
    setPositions(prev => prev.map(p => 
      p.id === updatedPosition.id ? updatedPosition : p
    ));
    
    toast.success(
      `${updatedPosition.symbol} ${t('positionUpdated')}`,
      3000
    );
  }, [toast, t]);

  const openPartialCloseModal = useCallback((position) => {
    setClosingPosition(position);
    setPartialCloseModalOpen(true);
  }, []);

  const handlePartialClose = useCallback(async (positionId, volumeToClose, closedProfit) => {
    try {
      const position = positions.find(p => p.id === positionId);
      if (!position) return;

      const totalVolume = position.amount || position.volume;
      const remainingVolume = totalVolume - volumeToClose;

      // Create closed portion for history
      const closedPortion = {
        ...position,
        id: Date.now(),
        amount: volumeToClose,
        volume: volumeToClose,
        closeTime: new Date(),
        closePrice: position.currentPrice,
        profit: closedProfit,
        status: 'closed',
        partialClose: true
      };

      // Add to history
      setTradeHistory(prev => [closedPortion, ...prev]);

      if (remainingVolume > 0.01) {
        // Update remaining position
        const remainingProfit = position.profit - closedProfit;
        setPositions(prev => prev.map(p =>
          p.id === positionId
            ? {
                ...p,
                amount: remainingVolume,
                volume: remainingVolume,
                profit: remainingProfit
              }
            : p
        ));
      } else {
        // Close entire position if remaining is too small
        setPositions(prev => prev.filter(p => p.id !== positionId));
      }

    } catch (error) {
      console.error('Failed to partially close position:', error);
      throw error;
    }
  }, [positions]);

  const cancelOrder = useCallback(async (orderId) => {
    try {
      // Find the order details before removing
      const order = pendingOrders.find(o => o?.id === orderId);
      
      await tradingApi?.cancelOrder(orderId);
      
      // Remove from pending orders
      setPendingOrders(prev => prev?.filter(o => o?.id !== orderId));
      
      // Show success toast with order details
      if (order) {
        toast.success(
          `${order.symbol} ${t('orderCancelled')}\n${order.type?.toUpperCase()} ${order.amount} ${t('lot')} @ ${formatPrice(order.openPrice)}`,
          4000
        );
      } else {
        toast.success(t('orderCancelled'), 3000);
      }
    } catch (error) {
      console.error('Failed to cancel order:', error);
      toast.error(t('orderCancelFailed'), 4000);
    }
  }, [toast, t, pendingOrders, formatPrice]);

  const editOrder = useCallback((orderId) => {
    // TODO: Implement order editing functionality
  }, []);

  // Pagination logic
  const totalPages = Math.ceil(combinedData?.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = combinedData?.slice(startIndex, startIndex + itemsPerPage);
  
  console.log('📄 PAGINATION:', {
    totalItems: combinedData?.length,
    currentPage,
    itemsPerPage,
    totalPages,
    paginatedItems: paginatedData?.length
  });

  // Reset to first page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const renderPositionsTable = useCallback(() => (
    <div className="space-y-4">
      {/* Search and Controls */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex-1 max-w-sm min-w-[200px]">
          <Input
            type="text"
            placeholder={t('searchSymbol')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            className="h-9"
          />
        </div>
        
        {/* Bulk Actions */}
        {selectedPositions.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              {selectedPositions.length} {t('selected')}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={closeSelectedPositions}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <Icon name="X" size={14} className="mr-1" />
              {t('closeSelected')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSelectedPositions([])}
            >
              {t('clearSelection')}
            </Button>
          </div>
        )}
        
        <div className="text-sm text-muted-foreground">
          {combinedData?.length} {t('items')}
        </div>
      </div>

      {/* Desktop Table View - Enhanced Design */}
      <div className="hidden md:block overflow-x-auto rounded-lg border border-border/50">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900">
              <th className="text-left py-3 px-3 font-semibold text-gray-700 dark:text-gray-300 w-12">
                <Checkbox
                  checked={selectedPositions.length === combinedData.length && combinedData.length > 0}
                  onChange={toggleSelectAll}
                  indeterminate={selectedPositions.length > 0 && selectedPositions.length < combinedData.length}
                />
              </th>
              <th className="text-left py-3 px-3 font-semibold text-gray-700 dark:text-gray-300 text-xs uppercase tracking-wide">{t('id')}</th>
              <th className="text-left py-3 px-3 font-semibold text-gray-700 dark:text-gray-300 text-xs uppercase tracking-wide">{t('symbol')}</th>
              <th className="text-left py-3 px-3 font-semibold text-gray-700 dark:text-gray-300 text-xs uppercase tracking-wide">{t('type')}</th>
              <th className="text-left py-3 px-3 font-semibold text-gray-700 dark:text-gray-300 text-xs uppercase tracking-wide">{t('amount')}</th>
              <th className="text-left py-3 px-3 font-semibold text-gray-700 dark:text-gray-300 text-xs uppercase tracking-wide">{t('openTime')}</th>
              <th className="text-left py-3 px-3 font-semibold text-gray-700 dark:text-gray-300 text-xs uppercase tracking-wide">{t('openPrice')}</th>
              <th className="text-left py-3 px-3 font-semibold text-gray-700 dark:text-gray-300 text-xs uppercase tracking-wide">{t('currentPrice')}</th>
              <th className="text-left py-3 px-3 font-semibold text-gray-700 dark:text-gray-300 text-xs uppercase tracking-wide">SL</th>
              <th className="text-left py-3 px-3 font-semibold text-gray-700 dark:text-gray-300 text-xs uppercase tracking-wide">TP</th>
              <th className="text-left py-3 px-3 font-semibold text-gray-700 dark:text-gray-300 text-xs uppercase tracking-wide">{t('profit')}</th>
              <th className="text-left py-3 px-3 font-semibold text-gray-700 dark:text-gray-300 text-xs uppercase tracking-wide">{t('swap')}</th>
              <th className="text-left py-3 px-3 font-semibold text-gray-700 dark:text-gray-300 text-xs uppercase tracking-wide">{t('commission')}</th>
              <th className="text-center py-3 px-3 font-semibold text-gray-700 dark:text-gray-300 text-xs uppercase tracking-wide">{t('actions')}</th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-950">
            {paginatedData?.map((item, index) => (
              <tr 
                key={item?.id} 
                className={`
                  border-b border-border/30 
                  transition-all duration-150
                  hover:bg-gradient-to-r hover:from-blue-50/50 hover:to-transparent 
                  dark:hover:from-blue-900/10 dark:hover:to-transparent
                  ${selectedPositions.includes(item?.id) ? 'bg-blue-50/30 dark:bg-blue-900/10' : ''}
                  ${index % 2 === 0 ? 'bg-gray-50/30 dark:bg-gray-900/20' : ''}
                `}
              >
                <td className="py-3 px-3">
                  <Checkbox
                    checked={selectedPositions.includes(item?.id)}
                    onChange={() => toggleSelectPosition(item?.id)}
                  />
                </td>
                <td className="py-3 px-3 text-foreground font-medium text-xs">{item?.id}</td>
                <td className="py-3 px-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-bold text-xs shadow-sm">
                      {item?.symbol?.substring(0, 2)}
                    </div>
                    <span className="font-bold text-foreground">{item?.symbol}</span>
                  </div>
                </td>
                <td className="py-3 px-3">
                  <span className={`
                    inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold
                    ${item?.type?.includes('buy') 
                      ? 'bg-gradient-to-r from-green-100 to-emerald-100 text-green-700 dark:from-green-900/30 dark:to-emerald-900/30 dark:text-green-400' 
                      : 'bg-gradient-to-r from-red-100 to-rose-100 text-red-700 dark:from-red-900/30 dark:to-rose-900/30 dark:text-red-400'
                    }
                  `}>
                    {item?.type?.includes('buy') ? '↑' : '↓'} {item?.type?.toUpperCase()}
                  </span>
                </td>
                <td className="py-3 px-3">
                  <span className="font-semibold text-foreground">{item?.amount || item?.volume}</span>
                  <span className="text-xs text-muted-foreground ml-1">{t('lot')}</span>
                </td>
                <td className="py-3 px-3 text-muted-foreground">
                  <div className="text-xs space-y-0.5">
                    <div className="font-medium">{formatDate(item?.openTime)}</div>
                    <div className="text-muted-foreground/70">{formatTime(item?.openTime)}</div>
                  </div>
                </td>
                <td className="py-3 px-3">
                  <div className="font-mono text-sm font-medium text-foreground bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
                    {formatPrice(item?.openPrice)}
                  </div>
                </td>
                <td className="py-3 px-3">
                  <div className="font-mono text-sm font-medium text-foreground bg-blue-50 dark:bg-blue-900/20 px-2 py-1 rounded border border-blue-200/50 dark:border-blue-800/50">
                    {formatPrice(item?.closePrice || item?.currentPrice)}
                  </div>
                </td>
                <td className="py-3 px-3 text-xs">
                  <span className="font-mono text-foreground bg-amber-50 dark:bg-amber-900/20 px-2 py-1 rounded">
                    {formatPrice(item?.stopLoss || item?.sl) || '--'}
                  </span>
                </td>
                <td className="py-3 px-3 text-xs">
                  <span className="font-mono text-foreground bg-green-50 dark:bg-green-900/20 px-2 py-1 rounded">
                    {formatPrice(item?.takeProfit || item?.tp) || '--'}
                  </span>
                </td>
                <td className="py-3 px-3">
                  <div className={`
                    inline-flex items-center px-3 py-1.5 rounded-lg font-bold text-sm
                    ${item?.profit >= 0 
                      ? 'bg-gradient-to-r from-green-100 to-emerald-100 text-green-700 dark:from-green-900/30 dark:to-emerald-900/30 dark:text-green-400 shadow-sm shadow-green-500/10' 
                      : 'bg-gradient-to-r from-red-100 to-rose-100 text-red-700 dark:from-red-900/30 dark:to-rose-900/30 dark:text-red-400 shadow-sm shadow-red-500/10'
                    }
                  `}>
                    {item?.profit >= 0 ? '+' : ''}${(item?.profit || 0)?.toFixed(2)}
                  </div>
                </td>
                <td className="py-3 px-3 text-xs font-mono text-muted-foreground">${(item?.swap || 0)?.toFixed(2)}</td>
                <td className="py-3 px-3 text-xs font-mono text-muted-foreground">${(item?.commission || 0)?.toFixed(2)}</td>
                <td className="py-3 px-3">
                  <div className="flex items-center justify-center gap-1">
                    {!item?.type?.includes('_') && (
                      <>
                        <button
                          onClick={() => editPosition(item)}
                          className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-lg transition-all hover:scale-110 hover:shadow-sm"
                          title={t('edit')}
                        >
                          <Icon name="Edit" size={16} />
                        </button>
                        <button
                          onClick={() => openPartialCloseModal(item)}
                          className="p-2 text-orange-600 hover:text-orange-700 hover:bg-orange-100 dark:hover:bg-orange-900/30 rounded-lg transition-all hover:scale-110 hover:shadow-sm"
                          title={t('partialClose')}
                        >
                          <Icon name="Scissors" size={16} />
                        </button>
                        <button
                          onClick={() => closePosition(item?.id)}
                          className="p-2 text-red-600 hover:text-red-700 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-all hover:scale-110 hover:shadow-sm"
                          title={t('close')}
                        >
                          <Icon name="X" size={16} />
                        </button>
                      </>
                    )}
                    {item?.type?.includes('_') && (
                      <>
                        <button
                          onClick={() => editOrder(item?.id)}
                          className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-lg transition-all hover:scale-110 hover:shadow-sm"
                          title={t('edit')}
                        >
                          <Icon name="Edit" size={16} />
                        </button>
                        <button
                          onClick={() => cancelOrder(item?.id)}
                          className="p-2 text-red-600 hover:text-red-700 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-all hover:scale-110 hover:shadow-sm"
                          title={t('cancel')}
                        >
                          <Icon name="X" size={16} />
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View - Finance Style */}
      <div className="block md:hidden space-y-3">
        {paginatedData && paginatedData.length > 0 && paginatedData.map((item) => (
          <div key={item?.id} className="bg-gradient-to-br from-card to-muted/20 rounded-xl p-4 border border-border/50 shadow-sm hover:shadow-md transition-all">
            {/* Header: Icon + Symbol + Type + P&L */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  item?.type?.toLowerCase()?.includes('buy') 
                    ? 'bg-green-500/10' 
                    : 'bg-red-500/10'
                }`}>
                  <Icon 
                    name={item?.type?.toLowerCase()?.includes('buy') ? 'TrendingUp' : 'TrendingDown'} 
                    size={20} 
                    className={item?.type?.toLowerCase()?.includes('buy') ? 'text-green-600' : 'text-red-600'} 
                  />
                </div>
                <div>
                  <h3 className="font-bold text-foreground text-base">{item?.symbol}</h3>
                  <span className={`text-xs font-medium ${getTypeColor(item?.type)}`}>
                    {item?.type?.toUpperCase()}
                  </span>
                </div>
              </div>
              <div className="text-right">
                <div className={`text-lg font-bold ${getProfitColor(item?.profit || 0)}`}>
                  {item?.profit >= 0 ? '+' : ''}${(item?.profit || 0)?.toFixed(2)}
                </div>
                <div className="text-xs text-muted-foreground">
                  {item?.volume || item?.amount} {t('lot')}
                </div>
              </div>
            </div>

            {/* Price Info Grid */}
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div className="bg-background/50 rounded-lg p-2">
                <div className="text-[10px] text-muted-foreground uppercase mb-0.5">{t('openPrice')}</div>
                <div className="font-mono font-semibold text-foreground text-sm">{formatPrice(item?.openPrice)}</div>
              </div>
              <div className="bg-background/50 rounded-lg p-2">
                <div className="text-[10px] text-muted-foreground uppercase mb-0.5">{t('currentPrice')}</div>
                <div className="font-mono font-semibold text-foreground text-sm">{formatPrice(item?.closePrice || item?.currentPrice)}</div>
              </div>
              <div className="bg-background/50 rounded-lg p-2">
                <div className="text-[10px] text-muted-foreground uppercase mb-0.5">SL</div>
                <div className="font-mono text-xs text-foreground">{formatPrice(item?.stopLoss || item?.sl) || '--'}</div>
              </div>
              <div className="bg-background/50 rounded-lg p-2">
                <div className="text-[10px] text-muted-foreground uppercase mb-0.5">TP</div>
                <div className="font-mono text-xs text-foreground">{formatPrice(item?.takeProfit || item?.tp) || '--'}</div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between pt-3 border-t border-border/30">
              <div className="text-xs text-muted-foreground">
                <div>{formatDate(item?.openTime)}</div>
                <div>{formatTime(item?.openTime)}</div>
              </div>
              <div className="flex items-center space-x-1">
                {!item?.type?.includes('_') && (
                  <>
                    <button
                      onClick={() => editPosition(item)}
                      className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                      title={t('edit')}
                    >
                      <Icon name="Edit" size={16} />
                    </button>
                    <button
                      onClick={() => openPartialCloseModal(item)}
                      className="p-2 text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/20 rounded-lg transition-colors"
                      title={t('partialClose')}
                    >
                      <Icon name="Scissors" size={16} />
                    </button>
                    <button
                      onClick={() => closePosition(item?.id)}
                      className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                      title={t('close')}
                    >
                      <Icon name="X" size={16} />
                    </button>
                  </>
                )}
                {item?.type?.includes('_') && (
                  <>
                    <button
                      onClick={() => editOrder(item?.id)}
                      className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                      title={t('edit')}
                    >
                      <Icon name="Edit" size={16} />
                    </button>
                    <button
                      onClick={() => cancelOrder(item?.id)}
                      className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                      title={t('cancel')}
                    >
                      <Icon name="X" size={16} />
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {paginatedData?.length === 0 && (
        <div className="text-center py-8 text-muted-foreground">
          <Icon name="Activity" size={32} className="mx-auto mb-2" />
          <p>{searchTerm ? t('noData') : t('noOpenPositions')}</p>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm">
          <div className="text-muted-foreground">
            {t('page')} {currentPage} {t('of')} {totalPages}
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 border border-border rounded text-foreground hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('previous')}
            </button>
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 border border-border rounded text-foreground hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('next')}
            </button>
          </div>
        </div>
      )}
    </div>
  ), [paginatedData, totalPages, currentPage, combinedData?.length, searchTerm, t, getTypeColor, formatDate, formatTime, formatPrice, getProfitColor, editOrder, cancelOrder, editPosition, closePosition, selectedPositions, toggleSelectPosition, toggleSelectAll, closeSelectedPositions, openPartialCloseModal]);

  const renderPendingOrders = useCallback(() => (
    <div className="space-y-4">
      {/* Desktop Table View - Enhanced Design */}
      <div className="hidden md:block overflow-x-auto rounded-lg border border-border/50">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900">
              <th className="text-left py-3 px-3 font-semibold text-gray-700 dark:text-gray-300 text-xs uppercase tracking-wide">{t('id')}</th>
              <th className="text-left py-3 px-3 font-semibold text-gray-700 dark:text-gray-300 text-xs uppercase tracking-wide">{t('symbol')}</th>
              <th className="text-left py-3 px-3 font-semibold text-gray-700 dark:text-gray-300 text-xs uppercase tracking-wide">{t('orderType')}</th>
              <th className="text-left py-3 px-3 font-semibold text-gray-700 dark:text-gray-300 text-xs uppercase tracking-wide">{t('volume')}</th>
              <th className="text-left py-3 px-3 font-semibold text-gray-700 dark:text-gray-300 text-xs uppercase tracking-wide">{t('orderPrice')}</th>
              <th className="text-left py-3 px-3 font-semibold text-gray-700 dark:text-gray-300 text-xs uppercase tracking-wide">{t('currentPrice')}</th>
              <th className="text-left py-3 px-3 font-semibold text-gray-700 dark:text-gray-300 text-xs uppercase tracking-wide">SL</th>
              <th className="text-left py-3 px-3 font-semibold text-gray-700 dark:text-gray-300 text-xs uppercase tracking-wide">TP</th>
              <th className="text-left py-3 px-3 font-semibold text-gray-700 dark:text-gray-300 text-xs uppercase tracking-wide">{t('placed')}</th>
              <th className="text-left py-3 px-3 font-semibold text-gray-700 dark:text-gray-300 text-xs uppercase tracking-wide">{t('expires')}</th>
              <th className="text-center py-3 px-3 font-semibold text-gray-700 dark:text-gray-300 text-xs uppercase tracking-wide">{t('actions')}</th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-950">
            {filteredPendingOrders?.map((order, index) => (
              <tr 
                key={order?.id}
                className={`
                  border-b border-border/30 
                  transition-all duration-150
                  hover:bg-gradient-to-r hover:from-amber-50/50 hover:to-transparent 
                  dark:hover:from-amber-900/10 dark:hover:to-transparent
                  ${index % 2 === 0 ? 'bg-gray-50/30 dark:bg-gray-900/20' : ''}
                `}
              >
                <td className="py-3 px-3 text-foreground font-medium text-xs">{order?.id}</td>
                <td className="py-3 px-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center text-white font-bold text-xs shadow-sm">
                      {order?.symbol?.substring(0, 2)}
                    </div>
                    <span className="font-bold text-foreground">{order?.symbol}</span>
                  </div>
                </td>
                <td className="py-3 px-3">
                  <span className={`
                    inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold
                    ${order?.type?.includes('buy') 
                      ? 'bg-gradient-to-r from-green-100 to-emerald-100 text-green-700 dark:from-green-900/30 dark:to-emerald-900/30 dark:text-green-400' 
                      : 'bg-gradient-to-r from-red-100 to-rose-100 text-red-700 dark:from-red-900/30 dark:to-rose-900/30 dark:text-red-400'
                    }
                  `}>
                    {order?.type?.includes('buy') ? '↑' : '↓'} {order?.type?.toUpperCase()}
                  </span>
                </td>
                <td className="py-3 px-3">
                  <span className="font-semibold text-foreground">{order?.amount || order?.volume}</span>
                  <span className="text-xs text-muted-foreground ml-1">{t('lot')}</span>
                </td>
                <td className="py-3 px-3">
                  <div className="font-mono text-sm font-medium text-foreground bg-amber-50 dark:bg-amber-900/20 px-2 py-1 rounded border border-amber-200/50 dark:border-amber-800/50">
                    {formatPrice(order?.orderPrice || order?.openPrice)}
                  </div>
                </td>
                <td className="py-3 px-3">
                  <div className="font-mono text-sm font-medium text-foreground bg-blue-50 dark:bg-blue-900/20 px-2 py-1 rounded">
                    {formatPrice(order?.currentPrice)}
                  </div>
                </td>
                <td className="py-3 px-3 text-xs">
                  <span className="font-mono text-foreground bg-red-50 dark:bg-red-900/20 px-2 py-1 rounded">
                    {formatPrice(order?.stopLoss || order?.sl) || '--'}
                  </span>
                </td>
                <td className="py-3 px-3 text-xs">
                  <span className="font-mono text-foreground bg-green-50 dark:bg-green-900/20 px-2 py-1 rounded">
                    {formatPrice(order?.takeProfit || order?.tp) || '--'}
                  </span>
                </td>
                <td className="py-3 px-3 text-muted-foreground">
                  <div className="text-xs space-y-0.5">
                    <div className="font-medium">{formatDate(order?.placeTime || order?.openTime)}</div>
                    <div className="text-muted-foreground/70">{formatTime(order?.placeTime || order?.openTime)}</div>
                  </div>
                </td>
                <td className="py-3 px-3">
                  <span className="text-xs px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 rounded font-medium">
                    {order?.expiry ? formatTime(order?.expiry) : t('gtc')}
                  </span>
                </td>
                <td className="py-3 px-3">
                  <div className="flex items-center justify-center gap-1">
                    <button
                      onClick={() => editOrder(order?.id)}
                      className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-lg transition-all hover:scale-110 hover:shadow-sm"
                      title={t('edit')}
                    >
                      <Icon name="Edit" size={16} />
                    </button>
                    <button
                      onClick={() => cancelOrder(order?.id)}
                      className="p-2 text-red-600 hover:text-red-700 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-all hover:scale-110 hover:shadow-sm"
                      title={t('cancel')}
                    >
                      <Icon name="X" size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View - Finance Style */}
      <div className="block md:hidden space-y-3">
        {filteredPendingOrders && filteredPendingOrders.length > 0 && filteredPendingOrders.map((order) => (
          <div key={order?.id} className="bg-gradient-to-br from-card to-muted/20 rounded-xl p-4 border border-border/50 shadow-sm hover:shadow-md transition-all">
            {/* Header: Icon + Symbol + Type + Order Price */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  order?.type?.toLowerCase()?.includes('buy') 
                    ? 'bg-green-500/10' 
                    : 'bg-red-500/10'
                }`}>
                  <Icon 
                    name="Clock" 
                    size={20} 
                    className="text-orange-600" 
                  />
                </div>
                <div>
                  <h3 className="font-bold text-foreground text-base">{order?.symbol}</h3>
                  <span className={`text-xs font-medium ${getTypeColor(order?.type)}`}>
                    {order?.type?.toUpperCase()}
                  </span>
                </div>
              </div>
              <div className="text-right">
                <div className="text-lg font-bold text-foreground">
                  ${formatPrice(order?.orderPrice || order?.openPrice)}
                </div>
                <div className="text-xs text-muted-foreground">
                  {order?.volume || order?.amount} {t('lot')}
                </div>
              </div>
            </div>

            {/* Price Info Grid */}
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div className="bg-background/50 rounded-lg p-2">
                <div className="text-[10px] text-muted-foreground uppercase mb-0.5">{t('orderPrice')}</div>
                <div className="font-mono font-semibold text-foreground text-sm">{formatPrice(order?.orderPrice || order?.openPrice)}</div>
              </div>
              <div className="bg-background/50 rounded-lg p-2">
                <div className="text-[10px] text-muted-foreground uppercase mb-0.5">{t('volume')}</div>
                <div className="font-mono font-semibold text-foreground text-sm">{order?.volume || order?.amount}</div>
              </div>
              <div className="bg-background/50 rounded-lg p-2">
                <div className="text-[10px] text-muted-foreground uppercase mb-0.5">SL</div>
                <div className="font-mono text-xs text-foreground">{formatPrice(order?.stopLoss || order?.sl) || '--'}</div>
              </div>
              <div className="bg-background/50 rounded-lg p-2">
                <div className="text-[10px] text-muted-foreground uppercase mb-0.5">TP</div>
                <div className="font-mono text-xs text-foreground">{formatPrice(order?.takeProfit || order?.tp) || '--'}</div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between pt-3 border-t border-border/30">
              <div className="text-xs text-muted-foreground">
                <div>{formatDate(order?.placeTime || order?.openTime)}</div>
                <div>{formatTime(order?.placeTime || order?.openTime)}</div>
              </div>
              <div className="flex items-center space-x-1">
                <button
                  onClick={() => editOrder(order?.id)}
                  className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                  title={t('edit')}
                >
                  <Icon name="Edit" size={16} />
                </button>
                <button
                  onClick={() => cancelOrder(order?.id)}
                  className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                  title={t('cancel')}
                >
                  <Icon name="X" size={16} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {filteredPendingOrders?.length === 0 && (
        <div className="text-center py-8 text-muted-foreground">
          <Icon name="Clock" size={32} className="mx-auto mb-2" />
          <p>{t('noOrdersAvailable')}</p>
        </div>
      )}
    </div>
  ), [filteredPendingOrders, t, getTypeColor, formatPrice, formatTime, formatDate, cancelOrder, editOrder]);

  const renderTradeHistory = useCallback(() => (
    <div className="space-y-4">
      {/* Desktop Table View - Enhanced Design */}
      <div className="hidden md:block overflow-x-auto rounded-lg border border-border/50">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900">
              <th className="text-left py-3 px-3 font-semibold text-gray-700 dark:text-gray-300 text-xs uppercase tracking-wide">{t('id')}</th>
              <th className="text-left py-3 px-3 font-semibold text-gray-700 dark:text-gray-300 text-xs uppercase tracking-wide">{t('symbol')}</th>
              <th className="text-left py-3 px-3 font-semibold text-gray-700 dark:text-gray-300 text-xs uppercase tracking-wide">{t('type')}</th>
              <th className="text-left py-3 px-3 font-semibold text-gray-700 dark:text-gray-300 text-xs uppercase tracking-wide">{t('volume')}</th>
              <th className="text-left py-3 px-3 font-semibold text-gray-700 dark:text-gray-300 text-xs uppercase tracking-wide">{t('openPrice')}</th>
              <th className="text-left py-3 px-3 font-semibold text-gray-700 dark:text-gray-300 text-xs uppercase tracking-wide">{t('closePrice')}</th>
              <th className="text-left py-3 px-3 font-semibold text-gray-700 dark:text-gray-300 text-xs uppercase tracking-wide">{t('openTime')}</th>
              <th className="text-left py-3 px-3 font-semibold text-gray-700 dark:text-gray-300 text-xs uppercase tracking-wide">{t('closeTime')}</th>
              <th className="text-left py-3 px-3 font-semibold text-gray-700 dark:text-gray-300 text-xs uppercase tracking-wide">{t('profit')}</th>
              <th className="text-left py-3 px-3 font-semibold text-gray-700 dark:text-gray-300 text-xs uppercase tracking-wide">{t('swap')}</th>
              <th className="text-left py-3 px-3 font-semibold text-gray-700 dark:text-gray-300 text-xs uppercase tracking-wide">{t('commission')}</th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-950">
            {tradeHistory?.map((trade, index) => (
              <tr 
                key={trade?.id}
                className={`
                  border-b border-border/30 
                  transition-all duration-150
                  hover:bg-gradient-to-r hover:from-gray-50 hover:to-transparent 
                  dark:hover:from-gray-800/50 dark:hover:to-transparent
                  ${index % 2 === 0 ? 'bg-gray-50/30 dark:bg-gray-900/20' : ''}
                `}
              >
                <td className="py-3 px-3">
                  <div className="flex items-center gap-2">
                    <span className="text-foreground font-medium text-xs">{trade?.id}</span>
                    {trade?.partialClose && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-gradient-to-r from-orange-100 to-amber-100 text-orange-700 dark:from-orange-900/30 dark:to-amber-900/30 dark:text-orange-400" title={t('partialClose')}>
                        <Icon name="Scissors" size={12} className="mr-0.5" /> {t('partial')}
                      </span>
                    )}
                  </div>
                </td>
                <td className="py-3 px-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-gray-500 to-gray-600 flex items-center justify-center text-white font-bold text-xs shadow-sm">
                      {trade?.symbol?.substring(0, 2)}
                    </div>
                    <span className="font-bold text-foreground">{trade?.symbol}</span>
                  </div>
                </td>
                <td className="py-3 px-3">
                  <span className={`
                    inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold
                    ${trade?.type?.includes('buy') 
                      ? 'bg-gradient-to-r from-green-100 to-emerald-100 text-green-700 dark:from-green-900/30 dark:to-emerald-900/30 dark:text-green-400' 
                      : 'bg-gradient-to-r from-red-100 to-rose-100 text-red-700 dark:from-red-900/30 dark:to-rose-900/30 dark:text-red-400'
                    }
                  `}>
                    {trade?.type?.includes('buy') ? '↑' : '↓'} {trade?.type?.toUpperCase()}
                  </span>
                </td>
                <td className="py-3 px-3">
                  <span className="font-semibold text-foreground">{trade?.volume || trade?.amount}</span>
                  <span className="text-xs text-muted-foreground ml-1">{t('lot')}</span>
                </td>
                <td className="py-3 px-3">
                  <div className="font-mono text-sm font-medium text-foreground bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
                    {formatPrice(trade?.openPrice)}
                  </div>
                </td>
                <td className="py-3 px-3">
                  <div className="font-mono text-sm font-medium text-foreground bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
                    {formatPrice(trade?.closePrice)}
                  </div>
                </td>
                <td className="py-3 px-3 text-muted-foreground">
                  <div className="text-xs space-y-0.5">
                    <div className="font-medium">{formatDate(trade?.openTime)}</div>
                    <div className="text-muted-foreground/70">{formatTime(trade?.openTime)}</div>
                  </div>
                </td>
                <td className="py-3 px-3 text-muted-foreground">
                  <div className="text-xs space-y-0.5">
                    <div className="font-medium">{formatDate(trade?.closeTime)}</div>
                    <div className="text-muted-foreground/70">{formatTime(trade?.closeTime)}</div>
                  </div>
                </td>
                <td className="py-3 px-3">
                  <div className={`
                    inline-flex items-center px-3 py-1.5 rounded-lg font-bold text-sm
                    ${trade?.profit >= 0 
                      ? 'bg-gradient-to-r from-green-100 to-emerald-100 text-green-700 dark:from-green-900/30 dark:to-emerald-900/30 dark:text-green-400 shadow-sm shadow-green-500/10' 
                      : 'bg-gradient-to-r from-red-100 to-rose-100 text-red-700 dark:from-red-900/30 dark:to-rose-900/30 dark:text-red-400 shadow-sm shadow-red-500/10'
                    }
                  `}>
                    {trade?.profit >= 0 ? '+' : ''}${(trade?.profit || 0)?.toFixed(2)}
                  </div>
                </td>
                <td className="py-3 px-3 text-xs font-mono text-muted-foreground">${(trade?.swap || 0)?.toFixed(2)}</td>
                <td className="py-3 px-3 text-xs font-mono text-muted-foreground">${(trade?.commission || 0)?.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View - Finance Style */}
      <div className="block md:hidden space-y-3">
        {filteredHistory && filteredHistory.length > 0 && filteredHistory.map((trade) => (
          <div key={trade?.id} className="bg-gradient-to-br from-card to-muted/20 rounded-xl p-4 border border-border/50 shadow-sm">
            {/* Header: Icon + Symbol + Type + P&L */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  (trade?.profit || 0) >= 0 
                    ? 'bg-green-500/10' 
                    : 'bg-red-500/10'
                }`}>
                  <Icon 
                    name={(trade?.profit || 0) >= 0 ? 'CheckCircle2' : 'XCircle'} 
                    size={20} 
                    className={(trade?.profit || 0) >= 0 ? 'text-green-600' : 'text-red-600'} 
                  />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold text-foreground text-base">{trade?.symbol}</h3>
                    {trade?.partialClose && (
                      <span className="text-[10px] bg-orange-100 dark:bg-orange-900/30 text-orange-600 px-1.5 py-0.5 rounded font-medium">
                        {t('partial')}
                      </span>
                    )}
                  </div>
                  <span className={`text-xs font-medium ${getTypeColor(trade?.type)}`}>
                    {trade?.type?.toUpperCase()}
                  </span>
                </div>
              </div>
              <div className="text-right">
                <div className={`text-lg font-bold ${getProfitColor(trade?.profit || 0)}`}>
                  {trade?.profit >= 0 ? '+' : ''}${(trade?.profit || 0)?.toFixed(2)}
                </div>
                <div className="text-xs text-muted-foreground">
                  {trade?.volume || trade?.amount} {t('lot')}
                </div>
              </div>
            </div>

            {/* Price Info Grid */}
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div className="bg-background/50 rounded-lg p-2">
                <div className="text-[10px] text-muted-foreground uppercase mb-0.5">{t('openPrice')}</div>
                <div className="font-mono font-semibold text-foreground text-sm">{formatPrice(trade?.openPrice)}</div>
              </div>
              <div className="bg-background/50 rounded-lg p-2">
                <div className="text-[10px] text-muted-foreground uppercase mb-0.5">{t('closePrice')}</div>
                <div className="font-mono font-semibold text-foreground text-sm">{formatPrice(trade?.closePrice)}</div>
              </div>
              <div className="bg-background/50 rounded-lg p-2">
                <div className="text-[10px] text-muted-foreground uppercase mb-0.5">Swap</div>
                <div className="font-mono text-xs text-foreground">${(trade?.swap || 0)?.toFixed(2)}</div>
              </div>
              <div className="bg-background/50 rounded-lg p-2">
                <div className="text-[10px] text-muted-foreground uppercase mb-0.5">{t('commission')}</div>
                <div className="font-mono text-xs text-foreground">${(trade?.commission || 0)?.toFixed(2)}</div>
              </div>
            </div>

            {/* Time Info */}
            <div className="flex items-center justify-between pt-3 border-t border-border/30 text-xs text-muted-foreground">
              <div>
                <div className="flex items-center gap-1">
                  <Icon name="ArrowUpCircle" size={12} />
                  <span>{formatDate(trade?.openTime)} {formatTime(trade?.openTime)}</span>
                </div>
                <div className="flex items-center gap-1 mt-1">
                  <Icon name="ArrowDownCircle" size={12} />
                  <span>{formatDate(trade?.closeTime)} {formatTime(trade?.closeTime)}</span>
                </div>
              </div>
              <div className="text-xs text-muted-foreground">
                ID: #{trade?.id}
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {filteredHistory?.length === 0 && (
        <div className="text-center py-8 text-muted-foreground">
          <Icon name="History" size={32} className="mx-auto mb-2" />
          <p>{t('noTradeHistory')}</p>
        </div>
      )}
    </div>
  ), [filteredHistory, t, getTypeColor, formatPrice, getProfitColor, formatTime, formatDate]);

  const renderFinanceTab = useCallback(() => {
    // Mock transaction data
    const transactions = [
      {
        id: 'TXN-001',
        type: 'deposit',
        description: 'Bank Transfer',
        amount: 5000.00,
        currency: 'USD',
        status: 'completed',
        date: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
        reference: 'DEP-20250112-001'
      },
      {
        id: 'TXN-002',
        type: 'withdrawal',
        description: 'Wire Transfer',
        amount: -2500.00,
        currency: 'USD',
        status: 'completed',
        date: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
        reference: 'WTH-20250109-001'
      },
      {
        id: 'TXN-003',
        type: 'trade_profit',
        description: 'Trading Profit - EURUSD',
        amount: 125.50,
        currency: 'USD',
        status: 'completed',
        date: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
        reference: 'TRD-20250113-045'
      },
      {
        id: 'TXN-004',
        type: 'trade_loss',
        description: 'Trading Loss - GBPUSD',
        amount: -45.75,
        currency: 'USD',
        status: 'completed',
        date: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
        reference: 'TRD-20250111-032'
      },
      {
        id: 'TXN-005',
        type: 'commission',
        description: 'Trading Commission',
        amount: -15.00,
        currency: 'USD',
        status: 'completed',
        date: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
        reference: 'COM-20250113-001'
      },
      {
        id: 'TXN-006',
        type: 'swap',
        description: 'Overnight Swap - XAUUSD',
        amount: -8.25,
        currency: 'USD',
        status: 'completed',
        date: new Date(Date.now() - 12 * 60 * 60 * 1000),
        reference: 'SWP-20250113-001'
      },
      {
        id: 'TXN-007',
        type: 'deposit',
        description: 'Credit Card Deposit',
        amount: 1000.00,
        currency: 'USD',
        status: 'pending',
        date: new Date(Date.now() - 2 * 60 * 60 * 1000),
        reference: 'DEP-20250114-002'
      }
    ];

    const getTransactionIcon = (type) => {
      switch (type) {
        case 'deposit': return 'ArrowDownCircle';
        case 'withdrawal': return 'ArrowUpCircle';
        case 'trade_profit': return 'TrendingUp';
        case 'trade_loss': return 'TrendingDown';
        case 'commission': return 'Percent';
        case 'swap': return 'RefreshCw';
        default: return 'DollarSign';
      }
    };

    const getTransactionColor = (type, amount) => {
      if (amount > 0) return 'text-green-600 dark:text-green-400';
      if (amount < 0) return 'text-red-600 dark:text-red-400';
      return 'text-gray-600 dark:text-gray-400';
    };

    const getStatusBadge = (status) => {
      switch (status) {
        case 'completed':
          return <span className="text-xs px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded">{t('completed')}</span>;
        case 'pending':
          return <span className="text-xs px-2 py-1 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 rounded">{t('pending')}</span>;
        case 'failed':
          return <span className="text-xs px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded">{t('failed')}</span>;
        default:
          return null;
      }
    };

    return (
      <div className="space-y-4">
        {/* Desktop Table View */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-2 px-2 font-medium text-muted-foreground w-10"></th>
                <th className="text-left py-2 px-2 font-medium text-muted-foreground">{t('id')}</th>
                <th className="text-left py-2 px-2 font-medium text-muted-foreground">{t('type')}</th>
                <th className="text-left py-2 px-2 font-medium text-muted-foreground">{t('description')}</th>
                <th className="text-left py-2 px-2 font-medium text-muted-foreground">{t('amount')}</th>
                <th className="text-left py-2 px-2 font-medium text-muted-foreground">{t('date')}</th>
                <th className="text-left py-2 px-2 font-medium text-muted-foreground">{t('reference')}</th>
                <th className="text-left py-2 px-2 font-medium text-muted-foreground">{t('status')}</th>
              </tr>
            </thead>
            <tbody>
              {transactions?.map((txn) => (
                <tr key={txn?.id} className="border-b border-border/50 hover:bg-muted/20">
                  <td className="py-2 px-2">
                    <Icon 
                      name={getTransactionIcon(txn?.type)} 
                      size={18} 
                      className={getTransactionColor(txn?.type, txn?.amount)} 
                    />
                  </td>
                  <td className="py-2 px-2 text-foreground text-xs">{txn?.id}</td>
                  <td className="py-2 px-2">
                    <span className="text-xs font-medium text-foreground capitalize">
                      {txn?.type?.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="py-2 px-2 text-foreground">{txn?.description}</td>
                  <td className="py-2 px-2">
                    <span className={`font-medium font-mono ${getTransactionColor(txn?.type, txn?.amount)}`}>
                      {txn?.amount >= 0 ? '+' : ''}${Math.abs(txn?.amount)?.toFixed(2)}
                    </span>
                  </td>
                  <td className="py-2 px-2 text-muted-foreground">
                    <div className="text-xs">
                      <div>{formatDate(txn?.date)}</div>
                      <div>{formatTime(txn?.date)}</div>
                    </div>
                  </td>
                  <td className="py-2 px-2 text-muted-foreground text-xs font-mono">{txn?.reference}</td>
                  <td className="py-2 px-2">{getStatusBadge(txn?.status)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View */}
        <div className="md:hidden space-y-3">
          {transactions?.map((txn) => (
            <div key={txn?.id} className="bg-muted/30 rounded-lg p-4 border border-border/50">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <Icon 
                    name={getTransactionIcon(txn?.type)} 
                    size={20} 
                    className={getTransactionColor(txn?.type, txn?.amount)} 
                  />
                  <span className="font-semibold text-foreground">{txn?.description}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className={`font-medium ${getTransactionColor(txn?.type, txn?.amount)}`}>
                    {txn?.amount >= 0 ? '+' : ''}${Math.abs(txn?.amount)?.toFixed(2)}
                  </span>
                  {getStatusBadge(txn?.status)}
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-xs text-muted-foreground block">{t('type')}</span>
                  <span className="font-medium text-foreground capitalize">{txn?.type?.replace('_', ' ')}</span>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground block">{t('reference')}</span>
                  <span className="font-mono text-xs text-foreground">{txn?.reference}</span>
                </div>
              </div>

              <div className="flex justify-between items-center mt-3 pt-3 border-t border-border/30 text-xs text-muted-foreground">
                <span>{formatDate(txn?.date)} {formatTime(txn?.date)}</span>
                <span className="text-xs">ID: {txn?.id}</span>
              </div>
            </div>
          ))}
        </div>

        {transactions?.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            <Icon name="CreditCard" size={32} className="mx-auto mb-2" />
            <p>{t('noTransactions')}</p>
          </div>
        )}
      </div>
    );
  }, [t, formatPrice, formatTime, formatDate, getTypeColor]);

  if (loading) {
    return (
      <div className={`h-full bg-card flex items-center justify-center ${isRTL ? 'rtl' : ''}`}>
        <div className="text-center">
          <Icon name="Loader" size={32} className="text-muted-foreground mb-4 animate-spin" />
          <p className="text-muted-foreground">{t('loadingPositions')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`h-full bg-card flex flex-col ${isRTL ? 'rtl' : ''}`}>
      {/* Header with Connection Status */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-foreground">{t('openPositionsAndPending')}</h2>
          <div className="flex items-center space-x-1">
            <div className={`w-2 h-2 rounded-full ${
              connectionStatus === 'connected' ? 'bg-green-500' : 
              connectionStatus === 'connecting' ? 'bg-yellow-500' : 'bg-red-500'
            }`}></div>
            <span className="text-xs text-muted-foreground">
              {connectionStatus === 'connected' ? 'Live' : connectionStatus}
            </span>
          </div>
        </div>

        {/* Tabs - Desktop Version */}
        <div className="hidden md:flex space-x-3 bg-gray-100 dark:bg-gray-800/50 p-2">
          <button
            onClick={() => setActiveTab('positions')}
            className={`flex items-center space-x-2 py-2.5 px-4 text-sm font-semibold rounded-md transition-all ${
              activeTab === 'positions' 
                ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 shadow-sm' 
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
            }`}
          >
            <Icon 
              name="BookOpen" 
              size={18} 
              className={activeTab === 'positions' ? 'text-blue-400' : 'text-gray-600 dark:text-gray-400'} 
            />
            <span>{t('openPositions')}</span>
          </button>
          
          <button
            onClick={() => setActiveTab('orders')}
            className={`flex items-center space-x-2 py-2.5 px-4 text-sm font-semibold rounded-md transition-all ${
              activeTab === 'orders' 
                ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 shadow-sm' 
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
            }`}
          >
            <Icon 
              name="FileText" 
              size={18} 
              className={activeTab === 'orders' ? 'text-blue-400' : 'text-gray-600 dark:text-gray-400'} 
            />
            <span>{t('pendingOrders')}</span>
          </button>
          
          <button
            onClick={() => setActiveTab('history')}
            className={`flex items-center space-x-2 py-2.5 px-4 text-sm font-semibold rounded-md transition-all ${
              activeTab === 'history' 
                ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 shadow-sm' 
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
            }`}
          >
            <Icon 
              name="XCircle" 
              size={18} 
              className={activeTab === 'history' ? 'text-blue-400' : 'text-gray-600 dark:text-gray-400'} 
            />
            <span>{t('closedPositions')}</span>
          </button>
          
          <button
            onClick={() => setActiveTab('finance')}
            className={`flex items-center space-x-2 py-2.5 px-4 text-sm font-semibold rounded-md transition-all ${
              activeTab === 'finance' 
                ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 shadow-sm' 
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
            }`}
          >
            <Icon 
              name="CreditCard" 
              size={18} 
              className={activeTab === 'finance' ? 'text-blue-400' : 'text-gray-600 dark:text-gray-400'} 
            />
            <span>{t('finance')}</span>
          </button>
        </div>

        {/* Tabs - Mobile Version (Scrollable) */}
        <div className="md:hidden overflow-x-auto bg-gray-100 dark:bg-gray-800/50 p-2 -mx-4 px-4">
          <div className="flex space-x-2 min-w-max">
            <button
              onClick={() => setActiveTab('positions')}
              className={`flex flex-col items-center justify-center py-2 px-3 text-xs font-semibold rounded-md transition-all whitespace-nowrap ${
                activeTab === 'positions' 
                  ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 shadow-sm' 
                  : 'text-gray-600 dark:text-gray-400'
              }`}
            >
              <Icon 
                name="BookOpen" 
                size={20} 
                className={`mb-1 ${activeTab === 'positions' ? 'text-blue-400' : 'text-gray-600 dark:text-gray-400'}`} 
              />
              <span>{t('open')}</span>
            </button>
            
            <button
              onClick={() => setActiveTab('orders')}
              className={`flex flex-col items-center justify-center py-2 px-3 text-xs font-semibold rounded-md transition-all whitespace-nowrap ${
                activeTab === 'orders' 
                  ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 shadow-sm' 
                  : 'text-gray-600 dark:text-gray-400'
              }`}
            >
              <Icon 
                name="FileText" 
                size={20} 
                className={`mb-1 ${activeTab === 'orders' ? 'text-blue-400' : 'text-gray-600 dark:text-gray-400'}`} 
              />
              <span>{t('pending')}</span>
            </button>
            
            <button
              onClick={() => setActiveTab('history')}
              className={`flex flex-col items-center justify-center py-2 px-3 text-xs font-semibold rounded-md transition-all whitespace-nowrap ${
                activeTab === 'history' 
                  ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 shadow-sm' 
                  : 'text-gray-600 dark:text-gray-400'
              }`}
            >
              <Icon 
                name="XCircle" 
                size={20} 
                className={`mb-1 ${activeTab === 'history' ? 'text-blue-400' : 'text-gray-600 dark:text-gray-400'}`} 
              />
              <span>{t('closed')}</span>
            </button>
            
            <button
              onClick={() => setActiveTab('finance')}
              className={`flex flex-col items-center justify-center py-2 px-3 text-xs font-semibold rounded-md transition-all whitespace-nowrap ${
                activeTab === 'finance' 
                  ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 shadow-sm' 
                  : 'text-gray-600 dark:text-gray-400'
              }`}
            >
              <Icon 
                name="CreditCard" 
                size={20} 
                className={`mb-1 ${activeTab === 'finance' ? 'text-blue-400' : 'text-gray-600 dark:text-gray-400'}`} 
              />
              <span>{t('finance')}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {activeTab === 'positions' && renderPositionsTable()}
        {activeTab === 'orders' && renderPendingOrders()}
        {activeTab === 'history' && renderTradeHistory()}
        {activeTab === 'finance' && renderFinanceTab()}
      </div>

      {/* Edit Position Modal */}
      <EditPositionModal
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        position={editingPosition}
        onSave={handleSavePosition}
      />

      {/* Partial Close Modal */}
      <PartialCloseModal
        isOpen={partialCloseModalOpen}
        onClose={() => setPartialCloseModalOpen(false)}
        position={closingPosition}
        onPartialClose={handlePartialClose}
      />
    </div>
  );
};

export default PositionsPanel;